import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { useTranslation } from 'react-i18next';
import { useRemoveBankAccount } from '@/hooks/payments/bankAccount/useRemoveBankAccount';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import { ConfirmRemoveDialog } from './ConfirmRemoveDialog';

interface RemoveBankAccountDialogProps {
  /** The account pending removal; `null` closes the dialog. Controlled like `RemoveCardDialog`. */
  account: BankAccountAccount | null;
  onClose: () => void;
  /** Called after a successful removal — the caller decides toast/close, same contract as Edit. */
  onSuccess?: () => void;
}

/** Confirms bank account removal. Supplies account-specific copy/preview/mutation to `ConfirmRemoveDialog`. */
export function RemoveBankAccountDialog({ account, onClose, onSuccess }: RemoveBankAccountDialogProps) {
  const { t } = useTranslation('members');
  const mutation = useRemoveBankAccount({ onSuccess });

  return (
    <ConfirmRemoveDialog
      open={account !== null}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
      isPending={mutation.isPending}
      title={t('payments.pages.paymentAccounts.bankAccounts.removeConfirmTitle')}
      description={t('payments.pages.paymentAccounts.bankAccounts.removeConfirmDescription')}
      preview={
        account && (
          <>
            <span className="text-muted-foreground">
              <Icon name="landmark" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">{`···· ${account.last6}`}</span>
            </span>
          </>
        )
      }
      cancelLabel={t('payments.pages.paymentAccounts.bankAccounts.removeConfirmCancel')}
      confirmLabel={t('payments.pages.paymentAccounts.bankAccounts.removeConfirmAction')}
      confirmSubmittingLabel={t('payments.pages.paymentAccounts.bankAccounts.removeConfirmSubmitting')}
      onCancel={onClose}
      onConfirm={() => account && mutation.submit(account.id)}
    />
  );
}
